import React, { useEffect, useState } from "react";
import { Card, Button, Space, Typography, Spin, message } from "antd";
import axios from "axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useAllData } from "../../screens/newSearch/context/AllDataContext";
import { getS3Data } from "../notificationsAPI";
import { useNotifications } from "../NotificationContext";
import '../BulkSignals.css';

const { Title, Text } = Typography;

const styles = {
  container: {
    width: '100%'
  },
  label: {
    color: 'rgba(255, 255, 255, 0.85)',
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 500
  },
  value: {
    color: 'rgba(255, 255, 255, 0.65)',
    fontSize: 14
  },
  buttonGroup: {
    display: 'flex',
    gap: 8,
    marginTop: 16
  },
  markReadButton: {
    backgroundColor: '#5c6cfa',
    color: '#fff',
    width: '100%',
    '&:hover': {
      backgroundColor: '#4a57d9',
      borderColor: '#4a57d9'
    }
  },
  investigateButton: {
    backgroundColor: 'rgba(96, 165, 250, 0.9)',
    color: '#fff',
    width: '100%',
    '&:hover': {
      backgroundColor: 'rgba(96, 165, 250, 1)',
      borderColor: 'rgba(96, 165, 250, 1)'
    }
  }
};

function BulkSignals({ bulkSignals, onStatusChange }) {
  const { user } = useAuthContext();
  const { setAllData } = useAllData();
  const [loadingSignals, setLoadingSignals] = useState({});
  const [loadedCounts, setLoadedCounts] = useState({});
  const { openNotifications } = useNotifications();
  const [markingRead, setMarkingRead] = useState({});

  const unreadSignals = bulkSignals.filter(signal => signal.status !== "read");

  useEffect(() => {
    const interval = setInterval(() => {
      unreadSignals.forEach(async (signal) => {
        if (signal.status === "pending") {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/api/v2/bulkSignals/checkExportStatus`,
              { requestId: signal.requestId },
              {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                  "Content-Type": "application/json",
                },
              }
            );
  
            if (response.data.requestStatus === "DONE") {
              if (response.data.message?.includes('no results were found')) {
                await axios.post(
                  `${process.env.REACT_APP_BASE_URL}/api/v2/notifications/updateStatus`,
                  { 
                    requestId: signal.requestId,
                    status: 'NO DATA'
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${user.token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                onStatusChange();
              } else if (signal.status !== "UPLOADED") {
                onStatusChange();
                openNotifications();
              }
            }
          } catch (error) {
            console.error("Error checking export status:", error);
          }
        }
      });
    }, 10000);
  
    return () => clearInterval(interval);
  }, [unreadSignals, onStatusChange, user.token, openNotifications]);

  const handleMarkAsRead = async (signal) => {
    setMarkingRead(prev => ({ ...prev, [signal._id]: true }));
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v2/notifications/updateStatus`,
        {
          requestId: signal.requestId,
          status: "read",
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Notification marked as read");
      onStatusChange();
    } catch (error) {
      console.error("Error marking as read:", error);
      message.error("Failed to mark as read");
    } finally {
      setMarkingRead(prev => ({ ...prev, [signal._id]: false }));
    }
  };

  const handleAddToInvestigation = async (signal) => {
    setLoadingSignals((prev) => ({ ...prev, [signal._id]: true }));
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v2/bulkSignals/createBulkSignal`,
        { requestId: signal.requestId },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        signal.s3Key &&
        (typeof signal.s3Key === "string" || signal.s3Key.length > 0)
      ) {
        const s3Response = await getS3Data(user, signal.s3Key, 2, 0);
        setLoadedCounts((prev) => ({ ...prev, [signal._id]: 2 }));

        const updatedBulkSignal = {
          ...response.data.bulkSignal,
          data: s3Response.data.map((item) => item.data),
          totalFiles: s3Response.total,
          hasMore: s3Response.hasMore,
          type: 'bulkSignals'
        };
        console.log("Updated bulk signal:", updatedBulkSignal);

        setAllData((prevAllData) => ({
          ...prevAllData,
          bulkSignals: [...prevAllData.bulkSignals, updatedBulkSignal],
        }));
      }
    } catch (error) {
      console.error("Error adding signal to investigation:", error);
    } finally {
      setLoadingSignals((prev) => ({ ...prev, [signal._id]: false }));
    }
  };

  return (
    <Space direction="vertical" style={styles.container}>
      {unreadSignals.map((signal, index) => (
        <Card
          key={signal._id || index}
          className="custom-notification-card"
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <Text strong style={styles.label}>Notification Name: </Text>
              <Text style={styles.value} block>{signal.name}</Text>
            </div>
            
            <div>
              <Text strong style={styles.label}>Request ID: </Text>
              <Text style={styles.value} block>{signal.requestId}</Text>
            </div>
            
            <div>
              <Text strong style={styles.label}>Status: </Text>
              <Text style={styles.value} block>{signal.status}</Text>
            </div>

            <div style={styles.buttonGroup}>
              <Button 
                className="mark-read-button"
                size="middle"
                block
                loading={markingRead[signal._id]}
                onClick={() => handleMarkAsRead(signal)}
              >
                Mark As Read
              </Button>
              
              <Button
                className="investigate-button"
                size="middle"
                block
                loading={loadingSignals[signal._id]}
                disabled={signal.status !== "UPLOADED"}
                onClick={() => handleAddToInvestigation(signal)}
              >
                Add to Investigation
              </Button>
            </div>
          </Space>
        </Card>
      ))}
    </Space>
  );
}

export default BulkSignals;